'use client';

import { useState, useEffect } from 'react';

interface SettingsPanelProps {
  profileId: string;
  isOpen: boolean;
  onClose: () => void;
  initialCalendarUrl?: string;
  onSaved: () => void;
}

export default function SettingsPanel({
  profileId,
  isOpen,
  onClose,
  initialCalendarUrl = '',
  onSaved,
}: SettingsPanelProps) {
  const [calendarUrl, setCalendarUrl] = useState(initialCalendarUrl);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setCalendarUrl(initialCalendarUrl);
  }, [initialCalendarUrl, isOpen]);

  const handleSave = async () => {
    setError(null);
    setSuccess(false);
    setSaving(true);
    try {
      const res = await fetch('/api/calendar/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profileId, calendarUrl: calendarUrl.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || res.statusText || 'Failed to save');
        return;
      }
      setSuccess(true);
      onSaved();
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="settings-panel">
      <div className="settings-panel__header">
        <h3 className="settings-panel__title">Settings</h3>
        <button type="button" className="settings-panel__close" onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>
      <div className="settings-panel__body">
        <label className="settings-panel__label">Calendar URL (iCal subscription)</label>
        <input
          type="url"
          className="settings-panel__input"
          value={calendarUrl}
          onChange={(e) => setCalendarUrl(e.target.value)}
          placeholder="https://..."
          disabled={saving}
        />
        <p className="settings-panel__hint">
          Paste the .ics subscription URL from your school or calendar app. The URL is only used on the server to sync assignments.
        </p>
        {error && <p className="settings-panel__error">{error}</p>}
        {success && <p className="settings-panel__success">Saved.</p>}
        <div className="settings-panel__actions">
          <button
            type="button"
            className="settings-panel__btn settings-panel__btn--primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="settings-panel__btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
